import {
  ContentMetricScoreByProductRecord,
  ContentMetricType,
  ContentScores,
  KeywordPerformanceScores,
  ToneType,
  VoiceAndToneMatchType,
  VoiceType
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import { KeywordFilter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/types';
import { GetEntityMetadataResponseRecord } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Total/hooks/useTotalTrafficByProduct';

export interface ChartDataAdapter<T> {
  transform(): T;
}

// Chart data types

// Product level utilization data types
interface BaseUtilizationData {
  type: 'character' | 'image' | 'video' | 'aPlusContent';
  title: string;
  content: string | ImageMetadata[] | VideoMetadata[] | boolean | null;
  currentUtilization: number | boolean;
}

// Specific interfaces for each label
interface CharacterUtilizationData extends BaseUtilizationData {
  type: 'character';
  content: string;
  maxUtilization: number;
  currentUtilization: number;
  score: number;
}

interface ImageUtilizationData extends BaseUtilizationData {
  type: 'image';
  content: ImageMetadata[] | null;
  maxUtilization: number;
  currentUtilization: number;
  score: number;
}

interface VideoUtilizationData extends BaseUtilizationData {
  type: 'video';
  content: VideoMetadata[] | null;
  maxUtilization: number;
  currentUtilization: number;
  score: number;
}

interface APlusContentUtilizationData extends BaseUtilizationData {
  type: 'aPlusContent';
  content: boolean;
  currentUtilization: boolean;
}

export interface ImageMetadata {
  url: string;
  altText: string;
  resolution: string;
  ratio: string;
}

export interface VideoMetadata {
  url: string;
  resolution: string;
  length: string; // TODO: or number, clarify with api data
}

export type UtilizationData =
  | CharacterUtilizationData
  | ImageUtilizationData
  | VideoUtilizationData
  | APlusContentUtilizationData;

// Product level accuracy data types
interface BaseAccuracyData {
  type: 'character' | 'image' | 'video';
  title: string;
  approvedContent: string | ImageMetadata | VideoMetadata;
  currentContent: string | ImageMetadata | VideoMetadata;
  isAccurate: boolean | null;
}

// Specific interfaces for each label
export interface CharacterAccuracyData extends BaseAccuracyData {
  type: 'character';
  approvedContent: string;
  currentContent: string;
}

interface ImageAccuracyData extends BaseAccuracyData {
  type: 'image';
  currentContent: ImageMetadata;
  approvedContent: ImageMetadata;
}

interface VideoAccuracyData extends BaseAccuracyData {
  type: 'video';
  currentContent: VideoMetadata;
  approvedContent: VideoMetadata;
}

export type AccuracyData = CharacterAccuracyData | ImageAccuracyData | VideoAccuracyData;

// Key metrics data types
export interface KeyMetric {
  id: string;
  name: string;
  value: number;
  change: number;
}

export const KeyMetricTitle = {
  aPlusScore: 'Enhanced Content',
  contentScore: 'Content Score',
  contentUtilizationScore: 'Content Utilization',
  contentAccuracyScore: 'Content Accuracy',
  descriptionScore: 'Description',
  featureBulletsScore: 'Bullets',
  imagesScore: 'Images',
  titleScore: 'Title',
  videosScore: 'Videos',
  keywordUsageScore: 'Keyword Usage',
  keywordProductivityScore: 'Keyword Productivity',
  keywordPopularityScore: 'Keyword Popularity',
  voiceMatchScore: 'Voice Match Score',
  toneMatchScore: 'Tone Match Score'
};

export const KeyMetricScoreTitle = {
  titleScore: 'Title Score',
  featureBulletsScore: 'Bullets Score',
  descriptionScore: 'Description Score',
  imagesScore: 'Image Score',
  videosScore: 'Video Score',
  aPlusScore: 'Enhanced Score'
};

// Product table grid data types
// TODO(tyan): Update type
export type TableGridData = any[];

export type VoiceScore = {
  score: number;
  expected: VoiceType;
  actual: VoiceType;
};

export type ToneScore = {
  score: number;
  expected: ToneType;
  actual: ToneType;
};

export type VoiceAndToneMatchAnalysisRow = {
  stacklineSku: string;
  product: Product;
  voice: VoiceScore;
  tone: ToneScore;
  voiceMatchScore: {
    value: number;
    prevValue: number;
    difference: number;
  };
  toneMatchScore: {
    value: number;
    prevValue: number;
    difference: number;
  };
  matchType: VoiceAndToneMatchType;
};

export type Product = GetEntityMetadataResponseRecord;

export type ProductRecord = ContentMetricScoreByProductRecord & {
  name: string;
  product: Product;
  comparisonTimePeriodScores: ContentScores;
};

export const KEYWORD_FILTER_TO_METRIC_TYPE = {
  [KeywordFilter.Usage]: ContentMetricType.KeywordUsageScore,
  [KeywordFilter.Productivity]: ContentMetricType.KeywordProductivityScore,
  [KeywordFilter.Popularity]: ContentMetricType.KeywordPopularityScore
};

// Product level keyword performance details data types
interface KeywordsResponse {
  sections: SectionKeywords[];
}

export interface SectionKeywords {
  name: string;
  content: KeywordPhrase[];
}

export interface KeywordPhrase {
  phrase: string;
  metadata?: KeywordsMetadata;
}

export interface KeywordsMetadata {
  totalWeight: number;
  weights?: KeywordWeight[];
}

export interface KeywordWeight {
  keyword: string;
  subcat_level_weight: number;
  sku_level_weight: number;
}

export interface KeywordBreakdownData {
  sections: SectionKeywords[];
  summaryScores: KeywordPerformanceScores;
}
